/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-02-17 10:20:52
 **/

import Paho from "paho-mqtt";
import omit from "lodash-es/omit";

import NetworkInterface from "@/network/NetworkInterface";
import type { Packet, PublishOptions } from "@/network/NetworkInterface";
import { Properties } from "@/properties";
import { JsonUtils } from "@/utilities";
import { EventHandler } from "@/eventhandler";

type Server = {
	host: string;
	port: number;
	encrypt: boolean;
};

export default class NetworkMQTT extends NetworkInterface {
	#mqtt: Paho.Client | null = null;
	#connected: boolean = false;

	constructor(
		private readonly id: string,
		private _options: (Paho.ConnectionOptions & Server) | null = null
	) {
		super();
	}

	public get mqtt() {
		if (!this.#mqtt) {
			throw new Error("mqtt is null");
		}
		return this.#mqtt;
	}

	public get options() {
		if (!this._options) {
			throw new Error("options is null");
		}
		return this._options;
	}

	public set options(value: Paho.ConnectionOptions & Server) {
		this._options = value;
		if (!this.options.onSuccess) {
			this.options.onSuccess = this.#on_connected.bind(this);
		}
		if (!this.options.onFailure) {
			this.options.onFailure = this.#on_failure.bind(this);
		}
	}

	public get is_connected(): boolean {
		return this.#connected;
	}

	public get topics() {
		return Properties.instance.get<string[]>("topics", [], { encrypt: this.options.encrypt });
	}

	public set topics(value: string[]) {
		Properties.instance.set("topics", value, { encrypt: this.options.encrypt, replace: true });
	}

	public connect(): Promise<NetworkInterface> {
		return new Promise<NetworkInterface>((resolve, reject) => {
			console.log("#NetworkMQTT#", "connecting...", this.options);

			this.#mqtt = new Paho.Client(this.options.host, this.options.port, this.id);
			this.#mqtt.onMessageArrived = this.on_message_received.bind(this);
			this.#mqtt.onConnectionLost = this.#on_connection_lost.bind(this);
			this.#mqtt.connect(omit(this.options, ["host", "port", "encrypt"]));

			this.once("network_connected", () => {
				console.log("#NetworkMQTT#", "connected");
				resolve(this);
			});
			this.once("network_connect_failed", () => {
				console.log("#NetworkMQTT#", "connect failed");
				reject(new Error("connect_failed"));
			});
		});
	}

	public disconnect(): Promise<void> {
		if (this.is_connected) {
			const topics = new Set(this.topics);
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			topics.size > 0 && this.unsubscribe(Array.from(topics));

			this.mqtt.disconnect();
		}
		return Promise.resolve();
	}

	public subscribe(topic: string | string[]): void {
		if (Array.isArray(topic)) {
			for (const _topic of topic) {
				this.subscribe(_topic);
			}
		} else {
			this.mqtt.subscribe(topic);
			this.topics = Array.from(new Set(this.topics).add(topic));
			console.log("#NetworkMQTT#", "subscribe", topic);
		}
	}

	public unsubscribe(topic: string | string[]) {
		if (Array.isArray(topic)) {
			for (const _topic of topic) {
				this.unsubscribe(_topic);
			}
		} else {
			this.mqtt.unsubscribe(topic);
			const topics = new Set(this.topics);
			topics.delete(topic);
			this.topics = Array.from(topics);
			console.log("#NetworkMQTT#", "unsubscribe", topic);
		}
	}

	public publish<T = any>(topic: string, payload: T, options: PublishOptions | null = null): Promise<Packet<T>> {
		try {
			const message = new Paho.Message(JsonUtils.to_string(payload) as string);
			message.destinationName = topic;
			if (options) {
				message.qos = (options.qos ?? 0) as Paho.Qos;
				if (options.retain) {
					message.retained = options.retain;
				}
			} else {
				message.qos = 0;
			}
			this.mqtt.send(message);
			return Promise.resolve({
				topic,
				payload
			} as Packet<T>);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	protected json_to_message(payload: unknown) {
		return new Paho.Message(JsonUtils.to_string(payload) as string);
	}

	#on_connected() {
		this.#connected = true;

		if (this.eventNames().includes("network_connected")) {
			this.emit("network_connected");
		}
		EventHandler.fire("$Network::Connected$");
	}

	#on_connection_lost() {
		if (!this.is_connected) {
			return;
		}

		this.#connected = false;

		if (this.eventNames().includes("network_disconnected")) {
			this.emit("network_disconnected");
		}
		EventHandler.fire("$Network::Disconnected$");
	}

	public on_message_received(message: Paho.Message) {
		const packet: Packet = {
			topic: message.destinationName,
			payload: JsonUtils.to_object(message.payloadString)
		};
		if (this.eventNames().includes("message_received")) {
			this.emit("message_received", packet);
		}
		EventHandler.fire("$Message::Received$", packet);
	}

	#on_failure() {
		if (this.eventNames().includes("network_connect_failed")) {
			this.emit("network_connect_failed");
		}
		EventHandler.fire("$Network::Failure$");
	}
}
